import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import styled from 'styled-components';
import {
  Modal,
  Button,
  Grid,
  Message,
  Icon,
  Flex,
  Checkbox,
  Segment,
} from 'nallian-shared-ui/lib/components';
import { formatDate, formatDateTime } from 'nallian-shared-ui/lib/lib/dateTime';
import { useTranslation } from 'react-i18next';
import { DocumentStatus } from 'nallian-shared-ui/lib/layout/documents';
import apiRoutes from '../../../../../app/api/routes';
import ImageRedactor from './ImageRedactor';

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const MetadataGrid = styled(Grid)`
  &&& .row {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    align-items: center;
  }
`;

const Caption = styled.p`
  font-style: italic;
  color: #666;
  margin-top: 0.5rem;
`;

const DocumentPreviewModal = ({
  item,
  principalId,
  isRedacting,
  imageDataUrl,
  initialRedactionZones,
  onClose,
  onApprove,
}) => {
  const { t } = useTranslation(['app', 'shared']);
  const [imageError, setImageError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [redactedBase64, setRedactedBase64] = useState(null);
  const [showFinalPreview, setShowFinalPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleRedactedImageChange = useCallback(base64 => {
    setRedactedBase64(base64);
  }, []);

  const handleConfirmClick = useCallback(async () => {
    setConfirming(true);
    try {
      await onApprove(item, { redactedImage: redactedBase64 });
    } finally {
      setConfirming(false);
    }
  }, [item, redactedBase64, onApprove]);

  useEffect(() => {
    if (!item) return;

    // If we have imageDataUrl (redacting mode), use it directly instead of fetching
    if (isRedacting && imageDataUrl) {
      setPreviewUrl(imageDataUrl);
      return;
    }

    if (item.preview?.temporaryUrl) {
      setPreviewUrl(item.preview.temporaryUrl);
      return;
    }

    let cancelled = false;
    let timeoutId;
    const maxRetries = 6;
    const retryDelay = 3000;

    const fetchPreview = (attempt = 1) => {
      if (cancelled) return;
      setImageError(false);
      setPreviewUrl(null);
      setLoading(true);

      axios
        .post(apiRoutes.documentPreviewUrl(principalId, item.id))
        .then(res => {
          if (!cancelled) {
            setPreviewUrl(res.data.temporaryUrl);
            setLoading(false);
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < maxRetries) {
            timeoutId = setTimeout(() => fetchPreview(attempt + 1), retryDelay);
          } else {
            setImageError(true);
            setLoading(false);
          }
        });
    };

    fetchPreview();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [item, principalId, isRedacting, imageDataUrl]);

  useEffect(() => {
    if (!item) {
      onClose();
    }
  }, [item, onClose]);

  if (!item) return null;

  const showRedactingActions = isRedacting && onApprove;
  const expirationDate = item.expiresAt;
  const modalTitle = isRedacting ? t('documents.confirmUploadTitle') : t('documents.previewTitle');

  return (
    <Modal open onClose={onClose} closeOnDimmerClick={!showRedactingActions} size="small">
      <Modal.Header>{modalTitle}</Modal.Header>
      <Modal.Content>
        <MetadataGrid columns={2} margin="0 0 1rem 0">
          <Grid.Row>
            <Grid.Column>
              <strong>{t('documents.type')}:</strong>{' '}
              {t(`documents.type_${item.documentType}`, item.documentType)}
            </Grid.Column>
            <Grid.Column>
              <strong>{t('documents.expirationDate')}:</strong>{' '}
              {expirationDate ? formatDate(new Date(expirationDate)) : '-'}
            </Grid.Column>
          </Grid.Row>
          {!isRedacting && (
            <>
              <Grid.Row>
                <Grid.Column>
                  <strong>{t('documents.status')}:</strong> <DocumentStatus value={item.status} />
                </Grid.Column>
                <Grid.Column>
                  <strong>{t('documents.uploadedBy')}:</strong> {item.uploadedBy?.fullName || '-'}
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <strong>{t('documents.uploadedAt')}:</strong>{' '}
                  {item.createdOn ? formatDateTime(new Date(item.createdOn)) : '-'}
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </MetadataGrid>

        {loading && isRedacting && (
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            margin="0 0 1rem 0"
            padding="2rem"
          >
            <Icon name="spinner" loading size="large" />
            <Flex margin="1rem 0 0">{t('documents.redactingPlaceholder')}</Flex>
          </Flex>
        )}

        {loading && !isRedacting && (
          <Flex justifyContent="center" margin="0 0 1rem 0" padding="2rem">
            <Icon name="spinner" loading size="large" />
          </Flex>
        )}

        {!loading && !imageError && previewUrl && (
          <Flex
            direction="column"
            alignItems={isRedacting ? 'stretch' : 'center'}
            margin="0 0 1rem 0"
          >
            {isRedacting && imageDataUrl ? (
              <Segment>
                <ImageRedactor
                  src={imageDataUrl}
                  initialRects={initialRedactionZones}
                  onRedactedImageChange={handleRedactedImageChange}
                  previewMode={showFinalPreview}
                />
                <Segment secondary>
                  <Flex>
                    <Checkbox
                      toggle
                      label={`${t('documents.showFinalPreview')} (${t(
                        'documents.thisIsHowImageWillBeSaved'
                      )})`}
                      checked={showFinalPreview}
                      onChange={(_, data) => setShowFinalPreview(data.checked)}
                    />
                  </Flex>
                </Segment>
              </Segment>
            ) : (
              <>
                <PreviewImage
                  src={previewUrl}
                  alt={item.documentName}
                  onError={() => setImageError(true)}
                />
                <Caption>{t('documents.previewCaption')}</Caption>
              </>
            )}
          </Flex>
        )}

        {!loading && imageError && (
          <Message warning>
            <Icon name="warning sign" />
            {t('documents.previewError')}
          </Message>
        )}

        {!loading && showRedactingActions && (
          <>
            <Message warning>
              <div>
                <p>{t('documents.redactWarning.intro')}</p>
                <p>{t('documents.redactWarning.visibleInfo')}</p>
                <ul>
                  <li>
                    <strong>{t('documents.redactWarning.fullName')}</strong>
                  </li>
                  <li>
                    <strong>{t('documents.redactWarning.photo')}</strong>
                  </li>
                  <li>
                    <strong>{t('documents.redactWarning.expirationDate')}</strong>
                  </li>
                </ul>
                <p>{t('documents.redactWarning.mustBlackOut')}</p>
                <p>{t('documents.redactWarning.adjustInstructions')}</p>
              </div>
            </Message>
            <Flex margin="0.5rem 0 0">
              <Checkbox
                label={t('documents.redactConfirmCheckbox')}
                checked={acknowledged}
                onChange={(_, data) => setAcknowledged(data.checked)}
              />
            </Flex>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {showRedactingActions ? (
          <>
            <Button content={t('cancel')} onClick={onClose} />
            <Button
              color="green"
              content={t('documents.confirmUpload')}
              icon="check"
              loading={confirming}
              disabled={!acknowledged || !redactedBase64 || confirming}
              onClick={handleConfirmClick}
            />
          </>
        ) : (
          <Button content={t('close')} onClick={onClose} />
        )}
      </Modal.Actions>
    </Modal>
  );
};

DocumentPreviewModal.propTypes = {
  item: PropTypes.object,
  principalId: PropTypes.string.isRequired,
  isRedacting: PropTypes.bool,
  imageDataUrl: PropTypes.string,
  initialRedactionZones: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      w: PropTypes.number.isRequired,
      h: PropTypes.number.isRequired,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
};

DocumentPreviewModal.defaultProps = {
  item: null,
  isRedacting: false,
  imageDataUrl: undefined,
  initialRedactionZones: [],
  onApprove: undefined,
};

export default DocumentPreviewModal;
